import React, { useState } from 'react';
import { Form } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { sub } from 'date-fns';
import { Tooltip } from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import { InputMaskDate } from '../../../Helpers/Formatters';
import { CheckboxInput, TextInput, SelectInput } from '../../../Helpers/Inputs';
import BackNext from '../../Common/SubComponents/BackNext';
import GenderButtons from '../../Common/SubComponents/GenderButtons';
import { object, string, date, customValidators } from '../../../Helpers/Validators/customValidators';
import { getBasicInfo, setBasicInfo } from '../../../../sessionStorage';
import GetStateListForAflac from '../Helpers/StateListForAflac';
import { DELAWARE, NEWHAMPSHIRE, RHODE_ISLAND, VIRGINIA, WASHINGTON, NEW_JERSEY, NEW_YORK, GUAM, OHIO, PUERTO_RICO } from "../../../Helpers/Constants/States";
import { emptyStringToUndefined } from '../../../Helpers/Formatters/EmptyStringToUndefined';

export default function BasicInfo(props) {
    const states = GetStateListForAflac();
    const basicInfoInEligibleState = [DELAWARE, NEWHAMPSHIRE, RHODE_ISLAND, WASHINGTON, VIRGINIA, NEW_JERSEY, PUERTO_RICO, NEW_YORK, GUAM, OHIO];
    const formMethods = useForm({
        resolver: yupResolver(BasicInfo.ValidationSchema(props.ageSpecs, basicInfoInEligibleState)),
        mode: 'all',
        reValidateMode: 'all',
        defaultValues: props.formState,
    });

    const { handleSubmit } = formMethods;
    const handleValueChange = (field, value) => {
        props.onFieldChange(field, value);
    };
    const [error, setError] = useState(false);
    const onSubmit = () => {
        const basicInfoFromSessionStorage = getBasicInfo();
        basicInfoFromSessionStorage.gender = props.formState.gender;
        basicInfoFromSessionStorage.birthdate = props.formState.birthdate;
        basicInfoFromSessionStorage.state = props.formState.state;
        basicInfoFromSessionStorage.hsaAccount = props.formState.hsaAccount;
        setBasicInfo(basicInfoFromSessionStorage);
        props.onNext();
    };
    const onGetQuote = (props.formState.gender === '' || props.formState.birthdate === '' || props.formState.state === '');

    return (
        <div className="basic-info d-flex justify-content-center">
            <FormProvider {...formMethods}>
                <Form onSubmit={handleSubmit(onSubmit)} className="d-flex flex-column mt-4">
                    <BasicInfoForm formState={props.formState} onGetQuote={onGetQuote} handleValueChange={handleValueChange} onFieldChange={props.onFieldChange} hideBackNext={props.hideBackNext} logoBottom={props.logoBottom} states={states} error={error} />
                </Form>
            </FormProvider>
        </div>
    );
}

const label = (<span className="light-tooltip" style={{ boxShadow: 'none' }}>I have a Health Savings Account
    <Tooltip
        title='The easiest way to find out if you have a Health Savings Account (HSA) is to ask your Human Resources (HR) Department. You can also check your W-2 to see if you have the code "W" in box 12.'
        slotProps={{
            tooltip: {
                className: 'light-tooltip',
                sx: { fontSize: '14px' }
            },
        }}
        placement="top"
        enterTouchDelay={0}
    >
        <InfoIcon className="ms-2" />
    </Tooltip>
</span>);

function BasicInfoForm(props) {
    return (
        <div className="d-flex flex-column">
            <div className="text-danger text-center mb-3">{props.error}</div>
            <div className={`d-flex justify-content-center`}>
                <img src="/product-landing/supp/aflac-logo.png" alt="Aflac Logo" style={{ height: '73px', width: '160px' }} className="mb-2" />
            </div>

            <div className="m-0">
                <GenderButtons
                    name="gender"
                    selectedGender={props.formState.gender}
                    onClickMale={() => props.handleValueChange('gender', 'M')}
                    onClickFemale={() => props.handleValueChange('gender', 'F')}
                />
            </div>

            <div className='mb-3'>
                <TextInput
                    name="birthdate"
                    id="birthdate"
                    label="Date of Birth"
                    value={props.formState.birthdate}
                    onChange={(e) => props.handleValueChange('birthdate', e.target.value)}
                    InputProps={{
                        inputComponent: InputMaskDate,
                    }}
                />
            </div>

            <div className='mb-3'>
                <SelectInput
                    name="state"
                    id="state"
                    value={props.formState.state}
                    onChange={(e) => { props.handleValueChange('state', e.target.value); }}
                    label="State"
                    selectOptions={props.states}
                />
            </div>
            <div className="m-0">
                <CheckboxInput
                    name="hsaAccount"
                    id="hsaAccount"
                    checked={props.formState.hsaAccount}
                    onChange={(e) => props.onFieldChange('hsaAccount', e.target.checked)}
                    label={label}
                    color="primary"
                />
            </div>

            <BackNext
                disabled={props.onGetQuote}
                hideBack={true}
                onBack={props.onBack}
                continueLabel={{ text: "Get a Quote" }}
                className="w-100"
            />
        </div>
    );
}

BasicInfo.propTypes = {
    formState: PropTypes.object.isRequired,
    onNext: PropTypes.func,
    onFieldChange: PropTypes.func.isRequired,
    onBack: PropTypes.func,
};

BasicInfo.ValidationSchema = (ageSpecs, basicInfoInEligibleState) => {
    return object().shape({
        gender: string().required('Gender is required.'),
        birthdate: date().transform(emptyStringToUndefined)
            .typeError('Invalid date.')
            .max(sub(new Date(), { years: ageSpecs.min, days: ageSpecs.minAgeDays || 0 }), `Product not available due to age restrictions.`)
            .min(sub(new Date(), { years: ageSpecs.max }), `Product not available due to age restrictions.`)
            .required('Birthdate is required.'),
        state: customValidators.string().isValidState('State is required.', basicInfoInEligibleState),
    });
};

BasicInfo.DefaultFormState = (prefill = false, existingBasicInfo = null) => {
    return {
        gender: prefill ? 'M' : (existingBasicInfo && existingBasicInfo.gender) || '',
        birthdate: prefill ? '11/15/1970' : (existingBasicInfo && existingBasicInfo.birthdate) || '',
        state: prefill ? 'ID' : (existingBasicInfo && existingBasicInfo.state) || '',
        hsaAccount: prefill ? true : (existingBasicInfo && existingBasicInfo.hsaAccount) || false,
    };
};
